import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { axiosInstance, API } from "../App";
import { toast } from "sonner";
import { ArrowLeft, Plus, Pencil, Trash2, Package, Upload } from "lucide-react";

const Produk = ({ user }) => {
  const navigate = useNavigate();
  const [produk, setProduk] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentProduk, setCurrentProduk] = useState(null);
  const [importing, setImporting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [replaceAll, setReplaceAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  
  const [formData, setFormData] = useState({
    barcode: "",
    nama: "",
    satuan: "PCS",
    harga: "",
    harga_khusus: "",
    diskon1: 0,
    diskon2: 0,
  });

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/");
      return;
    }
    fetchProduk();
  }, [user, navigate]);

  const fetchProduk = async () => {
    try {
      const response = await axiosInstance.get(`${API}/produk`);
      // Sort by barcode A-Z
      const sortedData = response.data.sort((a, b) => 
        a.barcode.localeCompare(b.barcode)
      );
      setProduk(sortedData);
    } catch (error) {
      toast.error("Gagal memuat produk");
    } finally {
      setLoading(false);
    }
  };

  // Filter produk HANYA berdasarkan BARCODE (tidak nama barang)
  const filteredProduk = searchQuery
    ? produk.filter(item => item.barcode.toLowerCase().includes(searchQuery.toLowerCase()))
    : produk;

  // Pagination
  const totalPages = Math.ceil(filteredProduk.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProduk = filteredProduk.slice(startIndex, endIndex);

  // Reset to page 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editMode) {
        await axiosInstance.put(`${API}/produk/${currentProduk.id}`, formData);
        toast.success("Produk berhasil diperbarui");
      } else {
        await axiosInstance.post(`${API}/produk`, formData);
        toast.success("Produk berhasil ditambahkan");
      }

      setDialogOpen(false);
      resetForm();
      fetchProduk();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operasi gagal");
    }
  };

  const handleEdit = (produk) => {
    setCurrentProduk(produk);
    setFormData({
      barcode: produk.barcode,
      nama: produk.nama,
      satuan: produk.satuan || "PCS",
      harga: produk.harga,
      harga_khusus: produk.harga_khusus || 0,
      diskon1: produk.diskon1 || 0,
      diskon2: produk.diskon2 || 0,
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Yakin ingin menghapus produk ini?")) return;

    try {
      await axiosInstance.delete(`${API}/produk/${id}`);
      toast.success("Produk berhasil dihapus");
      fetchProduk();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Hapus gagal");
    }
  };

  const resetForm = () => {
    setFormData({
      barcode: "",
      nama: "",
      satuan: "PCS",
      harga: "",
      harga_khusus: "",
      diskon1: 0,
      diskon2: 0,
    });
    setEditMode(false);
    setCurrentProduk(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleImportExcel = async () => {
    if (!selectedFile) {
      toast.error("Pilih file Excel terlebih dahulu");
      return;
    }

    setImporting(true);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      // Add replace_all parameter to URL
      const url = replaceAll 
        ? `${API}/admin/import-produk?replace_all=true`
        : `${API}/admin/import-produk`;

      const response = await axiosInstance.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const msg = replaceAll 
        ? `Import berhasil! Semua produk lama dihapus, ${response.data.total_inserted + response.data.total_updated} produk baru di-import`
        : `Import berhasil! ${response.data.total_inserted} baru, ${response.data.total_updated} diupdate`;
      toast.success(msg);
      setImportDialogOpen(false);
      setSelectedFile(null);
      setReplaceAll(false);
      fetchProduk(); // Refresh list produk
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import gagal");
    } finally {
      setImporting(false);
    }
  };

  // Function handleDeleteAll dihapus - sudah tidak dipakai

  const formatRupiah = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Link to="/">
                <Button variant="ghost" size="sm" data-testid="back-button">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <img src="/logo-dumont.jpg" alt="Dumont" className="h-12 w-auto" />
              <div className="border-l border-gray-300 pl-3">
                <h1 className="text-2xl font-bold text-gray-800">Master Produk</h1>
                <p className="text-sm text-gray-600">Kelola data produk</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl">Daftar Produk</CardTitle>
              <div className="flex gap-2">
                {/* Button "Hapus Semua" dihapus - sudah tidak dipakai */}
                
                <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="border-indigo-500 text-indigo-600 hover:bg-indigo-50"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Import Excel
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Import Produk dari Excel</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="excel-file">Pilih File Excel</Label>
                        <Input
                          id="excel-file"
                          type="file"
                          accept=".xlsx,.xls"
                          onChange={handleFileChange}
                          disabled={importing}
                        />
                        <p className="text-xs text-gray-500">
                          Format: Excel (.xlsx atau .xls) dengan struktur yang sesuai
                        </p>
                        {selectedFile && (
                          <p className="text-sm text-green-600">
                            ✓ File terpilih: {selectedFile.name}
                          </p>
                        )}
                      </div>

                      {/* Checkbox Replace All */}
                      <div className="flex items-start space-x-3 p-4 bg-red-50 rounded-md border-2 border-red-400">
                        <input
                          type="checkbox"
                          id="replace-all"
                          checked={replaceAll}
                          onChange={(e) => setReplaceAll(e.target.checked)}
                          className="mt-1"
                        />
                        <div>
                          <label htmlFor="replace-all" className="text-sm font-bold text-red-900 cursor-pointer">
                            🚨 HAPUS SEMUA & REPLACE dengan File Excel
                          </label>
                          <p className="text-xs text-red-800 mt-2 font-medium">
                            ⚠️ PERHATIAN: Ini akan menghapus SEMUA {produk.length} produk yang ada (termasuk produk yang ditambah manual) dan menggantinya dengan data dari Excel!
                          </p>
                          <p className="text-xs text-gray-700 mt-2 bg-yellow-50 p-2 rounded border border-yellow-300">
                            💡 TIPS: Jika ada produk manual, JANGAN centang ini! Biarkan kosong agar produk manual tidak terhapus.
                          </p>
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setImportDialogOpen(false);
                          setSelectedFile(null);
                        }}
                        disabled={importing}
                      >
                        Batal
                      </Button>
                      <Button
                        onClick={handleImportExcel}
                        disabled={!selectedFile || importing}
                        className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                      >
                        {importing ? "Mengimport..." : "Import Sekarang"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      data-testid="add-produk-button"
                      onClick={resetForm}
                      className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Tambah Produk
                    </Button>
                  </DialogTrigger>
                <DialogContent className="max-w-md" data-testid="produk-dialog">
                  <DialogHeader>
                    <DialogTitle>{editMode ? "Edit Produk" : "Tambah Produk Baru"}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="barcode">Barcode</Label>
                      <Input
                        id="barcode"
                        data-testid="produk-barcode-input"
                        value={formData.barcode}
                        onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                        placeholder="Contoh: MIC0100999"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="nama">Nama Produk</Label>
                      <Input
                        id="nama"
                        data-testid="produk-nama-input"
                        value={formData.nama}
                        onChange={(e) => setFormData({ ...formData, nama: e.target.value })}
                        placeholder="Contoh: LC D10-40 MAB SWING LOCKCASE"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="satuan">Satuan</Label>
                      <Input
                        id="satuan"
                        value={formData.satuan}
                        onChange={(e) => setFormData({ ...formData, satuan: e.target.value.toUpperCase() })}
                        placeholder="Contoh: PCS, BOX, LUSIN"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="harga">Harga Default (Rp)</Label>
                      <Input
                        id="harga"
                        data-testid="produk-harga-input"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.harga}
                        onChange={(e) => setFormData({ ...formData, harga: e.target.value })}
                        placeholder="Contoh: 240000"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="harga_khusus">Harga Khusus (Opsional)</Label>
                      <Input
                        id="harga_khusus"
                        data-testid="produk-harga-khusus-input"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.harga_khusus}
                        onChange={(e) => setFormData({ ...formData, harga_khusus: e.target.value })}
                        placeholder="Kosongkan jika tidak ada"
                      />
                      <p className="text-xs text-gray-500">Untuk counter dengan tipe harga khusus</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="diskon1">Diskon 1 (%)</Label>
                        <Input
                          id="diskon1"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={formData.diskon1}
                          onChange={(e) => setFormData({ ...formData, diskon1: parseFloat(e.target.value) || 0 })}
                          placeholder="0"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="diskon2">Diskon 2 (%)</Label>
                        <Input
                          id="diskon2"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={formData.diskon2}
                          onChange={(e) => setFormData({ ...formData, diskon2: parseFloat(e.target.value) || 0 })}
                          placeholder="0"
                        />
                      </div>
                    </div>

                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setDialogOpen(false)}
                        data-testid="cancel-button"
                      >
                        Batal
                      </Button>
                      <Button
                        type="submit"
                        data-testid="submit-produk-button"
                        className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                      >
                        {editMode ? "Perbarui" : "Simpan"}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Search Box - HANYA BARCODE */}
            <div className="mb-6 space-y-2">
              <Label htmlFor="search" className="text-sm font-medium text-gray-700">
                🔍 Cari Berdasarkan Barcode
              </Label>
              <Input
                id="search"
                type="text"
                placeholder="Ketik barcode... (contoh: MIC0100999)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="max-w-md"
              />
              {searchQuery && (
                <p className="text-sm text-green-600">
                  ✓ Ditemukan {filteredProduk.length} produk dengan barcode "{searchQuery}"
                </p>
              )}
            </div>

            {/* Info Bar */}
            {!loading && produk.length > 0 && (
              <div className="mb-4 p-3 bg-blue-50 rounded-md">
                <p className="text-sm text-blue-800 font-medium">
                  📦 Total {produk.length} produk | Halaman {currentPage} dari {totalPages} | Diurutkan A-Z berdasarkan Barcode
                </p>
              </div>
            )}

            {loading ? (
              <div className="text-center py-8 text-gray-600">Memuat...</div>
            ) : produk.length === 0 ? (
              <div className="text-center py-8 text-gray-600">Belum ada produk</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Barcode</TableHead>
                      <TableHead>Nama Produk</TableHead>
                      <TableHead>Satuan</TableHead>
                      <TableHead className="text-right">Harga Default</TableHead>
                      <TableHead className="text-right">Harga Khusus</TableHead>
                      <TableHead className="text-center">Diskon 1 (%)</TableHead>
                      <TableHead className="text-center">Diskon 2 (%)</TableHead>
                      <TableHead className="text-right">Aksi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedProduk.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium text-blue-600">{item.barcode}</TableCell>
                        <TableCell>{item.nama}</TableCell>
                        <TableCell className="font-medium text-gray-700">{item.satuan || "PCS"}</TableCell>
                        <TableCell className="text-right">{formatRupiah(item.harga)}</TableCell>
                        <TableCell className="text-right">
                          {item.harga_khusus > 0 ? formatRupiah(item.harga_khusus) : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          {item.diskon1 ? `${item.diskon1}%` : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          {item.diskon2 ? `${item.diskon2}%` : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(item)}
                              data-testid={`edit-produk-${item.id}`}
                            >
                              <Pencil className="w-4 h-4 text-blue-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(item.id)}
                              data-testid={`delete-produk-${item.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Pagination Controls */}
            {!loading && filteredProduk.length > itemsPerPage && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t">
                <div className="text-sm text-gray-600">
                  Menampilkan {startIndex + 1}-{Math.min(endIndex, filteredProduk.length)} dari {filteredProduk.length} produk {searchQuery && `(filtered by barcode)`}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    ««
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    « Prev
                  </Button>
                  <div className="flex items-center px-4 text-sm font-medium">
                    {currentPage} / {totalPages}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next »
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    »»
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Produk;
